import {
  HouseholdMember,
  HouseholdMemberType,
  HouseholdWithBalances,
  UpdateHouseholdRequest,
} from "@fartherfinance/frontend/api/Households/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import { unknownBalanceValue } from "@src/constants/unknownBalanceValue";

import { HouseholdFormMember, HouseholdFormWithMembers } from "./types";

function createMemberRecord(
  members: HouseholdMember[]
): Record<ClientId, HouseholdFormMember> {
  return members.reduce((acc, member) => {
    return {
      ...acc,
      [member.clientId]: formatHouseholdMemberDefaultValue(member),
    };
  }, {} as Record<ClientId, HouseholdFormMember>);
}

export function formatHouseholdDefaultFormFields(
  household?: HouseholdWithBalances
): HouseholdFormWithMembers {
  if (!household) {
    return {
      householdName: "",
      establishedAt: null,
      pointsOfContact: {},
      members: {},
    };
  }

  const householdPOCs = createMemberRecord(
    filterPointsOfContact(household.members || [])
  );
  const householdMembers = createMemberRecord(
    filterMembers(household.members || [])
  );

  return {
    householdName: household.name,
    establishedAt: household.establishedAt
      ? new Date(household.establishedAt)
      : null,
    pointsOfContact: householdPOCs,
    members: householdMembers,
  };
}

export function getClientFullName(member: HouseholdMember): string {
  return member.firstName && member.lastName
    ? `${member.firstName} ${member.lastName}`
    : unknownBalanceValue;
}

function formatHouseholdMemberDefaultValue(member: HouseholdMember) {
  return {
    memberOption: {
      label: getClientFullName(member),
      value: member.clientId,
    },
    relationshipOption: {
      label: member.memberType,
      value: member.memberType,
    },
    pointOfContact: isPointOfContact(member),
  };
}

function filterPointsOfContact(members: HouseholdMember[]) {
  return members.filter((member) => isPointOfContact(member));
}

function filterMembers(members: HouseholdMember[]) {
  return members.filter((member) => !isPointOfContact(member));
}

export const DEFAULT_RELATIONSHIP = "Other individual";

export function formatAddHouseholdMemberResponse(member: HouseholdFormMember): {
  clientId: ClientId;
  memberType: HouseholdMemberType;
  pointOfContact?: boolean;
} {
  return {
    clientId: member.memberOption.value,
    memberType: (member.relationshipOption?.value ??
      DEFAULT_RELATIONSHIP) as HouseholdMemberType,
    pointOfContact: member.pointOfContact,
  };
}

export function formatEditHouseholdResponse(
  formData: HouseholdFormWithMembers
): UpdateHouseholdRequest {
  const members = {
    ...formData.pointsOfContact,
    ...formData.members,
  };

  return {
    name: formData.householdName,
    establishedAt: formData.establishedAt
      ? formData.establishedAt.toISOString()
      : undefined,
    members: Object.values(members).map((member) =>
      formatAddHouseholdMemberResponse(member)
    ),
  };
}

export const isPointOfContact = (member: HouseholdMember): boolean => {
  return member.pointOfContact || member.memberType === "Owner";
};
